import React, { useState } from "react";
import PropTypes from "prop-types";
import { Chip, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  formatRomeTimeLabel,
  isValidRomeTime,
  normalizeRomeTimeList,
} from "./utils/serviceConfigUtils";

const ScheduledTimesEditor = ({
  value = [],
  onChange,
  disabled = false,
}) => {
  const [draftHour, setDraftHour] = useState("");
  const [draftMinute, setDraftMinute] = useState("");
  const [error, setError] = useState("");

  const times = normalizeRomeTimeList(value);

  const addTime = () => {
    const hour = Number(String(draftHour).trim());
    const minute = Number(String(draftMinute).trim());
    if (!isValidRomeTime(hour, minute)) {
      setError("Inserire un orario valido (ora 0-23, minuti 0-59).");
      return;
    }
    const duplicate = times.some((item) => item.hour === hour && item.minute === minute);
    if (duplicate) {
      setError("Orario gia presente.");
      return;
    }
    onChange(
      [...times, { hour, minute }].sort(
        (left, right) => (left.hour * 60 + left.minute) - (right.hour * 60 + right.minute),
      ),
    );
    setDraftHour("");
    setDraftMinute("");
    setError("");
  };

  const removeTime = (hourToRemove, minuteToRemove) => {
    onChange(times.filter((item) => !(item.hour === hourToRemove && item.minute === minuteToRemove)));
    setError("");
  };

  const handleInputChange = (setter) => (event) => {
    setter(event.target.value);
    if (error) {
      setError("");
    }
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addTime();
    }
  };

  return (
    <div className="cm-config-recipients-editor">
      <div className="cm-config-recipients-input-row cm-config-time-input-row">
        <input
          className="cm-input cm-config-time-input"
          type="number"
          min={0}
          max={23}
          step={1}
          value={draftHour}
          onChange={handleInputChange(setDraftHour)}
          onKeyDown={handleInputKeyDown}
          disabled={disabled}
          placeholder="14"
          aria-label="Ora Europe/Rome"
          aria-invalid={Boolean(error)}
        />
        <span className="cm-config-time-separator" aria-hidden="true">:</span>
        <input
          className="cm-input cm-config-time-input"
          type="number"
          min={0}
          max={59}
          step={1}
          value={draftMinute}
          onChange={handleInputChange(setDraftMinute)}
          onKeyDown={handleInputKeyDown}
          disabled={disabled}
          placeholder="31"
          aria-label="Minuti Europe/Rome"
          aria-invalid={Boolean(error)}
        />
        <IconButton
          className="cm-config-recipients-add-btn"
          size="small"
          aria-label="Aggiungi orario"
          title="Aggiungi orario"
          onClick={addTime}
          disabled={disabled || draftHour === "" || draftMinute === ""}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </div>

      {error ? (
        <p className="cm-config-recipients-error" role="alert">
          {error}
        </p>
      ) : null}

      {times.length > 0 ? (
        <div className="cm-config-recipients-chips" role="list" aria-label="Orari import schedulato">
          {times.map((item) => (
            <Chip
              key={`${item.hour}-${item.minute}`}
              label={formatRomeTimeLabel(item.hour, item.minute)}
              size="small"
              className="cm-config-recipient-chip"
              role="listitem"
              onDelete={disabled ? undefined : () => removeTime(item.hour, item.minute)}
            />
          ))}
        </div>
      ) : (
        <p className="cm-config-recipients-empty">Nessun orario configurato.</p>
      )}
    </div>
  );
};

ScheduledTimesEditor.propTypes = {
  value: PropTypes.arrayOf(PropTypes.shape({
    hour: PropTypes.number.isRequired,
    minute: PropTypes.number.isRequired,
  })),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default ScheduledTimesEditor;
